/**
 * Endpoints del módulo de metas.
 *
 * Todo vive ya en EDSuite API (`/api/goals…`), portado del panel anterior.
 */

import { apiClient } from '@/lib/http/api-client';

import type {
  EvaluationPayload,
  Goal,
  GoalAuditLog,
  GoalChartData,
  GoalComparison,
  GoalDailyConsolidated,
  GoalEvaluation,
  GoalOption,
  GoalPayload,
  GoalProgress,
  GoalSettings,
  GoalStatus,
  GoalsDashboard,
  GoalsFilters,
  GoalsReports,
} from './metas.types';

export const metasKeys = {
  all: ['metas'] as const,
  lista: (filtros: GoalsFilters) => ['metas', 'lista', filtros] as const,
  detalle: (id: number) => ['metas', 'detalle', id] as const,
  dashboard: ['metas', 'dashboard'] as const,
  reportes: ['metas', 'reportes'] as const,
  progreso: (id: number) => ['metas', 'progreso', id] as const,
  grafico: (id: number) => ['metas', 'grafico', id] as const,
  comparativo: (id: number) => ['metas', 'comparativo', id] as const,
  consolidado: (id: number) => ['metas', 'consolidado', id] as const,
  evaluaciones: (id: number) => ['metas', 'evaluaciones', id] as const,
  historial: (id: number) => ['metas', 'historial', id] as const,
  auditoria: (id: number) => ['metas', 'auditoria', id] as const,
  configuracion: ['metas', 'configuracion'] as const,
  productos: ['metas', 'productos'] as const,
  lados: ['metas', 'lados'] as const,
  usuarios: ['metas', 'usuarios'] as const,
} as const;

/** Quita las claves vacías para no enviar filtros en blanco. */
function limpiarFiltros(filtros: GoalsFilters): Record<string, string | number> {
  const query: Record<string, string | number> = {};

  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor === '' || valor === undefined || valor === null) continue;
    query[clave] = valor;
  }

  return query;
}

export const metasApi = {
  listar: (filtros: GoalsFilters, signal?: AbortSignal): Promise<Goal[]> =>
    apiClient.get<Goal[]>('/api/goals', { query: limpiarFiltros(filtros), signal }),

  detalle: (id: number, signal?: AbortSignal): Promise<Goal> =>
    apiClient.get<Goal>(`/api/goals/${id}`, { signal }),

  dashboard: (signal?: AbortSignal): Promise<GoalsDashboard> =>
    apiClient.get<GoalsDashboard>('/api/goals/dashboard', { signal }),

  reportes: (signal?: AbortSignal): Promise<GoalsReports> =>
    apiClient.get<GoalsReports>('/api/goals/reports', { signal }),

  progreso: (id: number, signal?: AbortSignal): Promise<GoalProgress> =>
    apiClient.get<GoalProgress>(`/api/goals/${id}/progress`, { signal }),

  grafico: (id: number, signal?: AbortSignal): Promise<GoalChartData> =>
    apiClient.get<GoalChartData>(`/api/goals/${id}/chart_data`, { signal }),

  comparativo: (id: number, signal?: AbortSignal): Promise<GoalComparison> =>
    apiClient.get<GoalComparison>(`/api/goals/${id}/comparison`, { signal }),

  consolidado: (id: number, signal?: AbortSignal): Promise<GoalDailyConsolidated> =>
    apiClient.get<GoalDailyConsolidated>(`/api/goals/${id}/daily_consolidated`, { signal }),

  evaluaciones: (id: number, signal?: AbortSignal): Promise<GoalEvaluation[]> =>
    apiClient.get<GoalEvaluation[]>(`/api/goals/${id}/evaluations`, { signal }),

  historial: (id: number, signal?: AbortSignal): Promise<GoalEvaluation[]> =>
    apiClient.get<GoalEvaluation[]>(`/api/goals/${id}/change_history`, { signal }),

  auditoria: (id: number, signal?: AbortSignal): Promise<GoalAuditLog[]> =>
    apiClient.get<GoalAuditLog[]>(`/api/goals/${id}/audit_logs`, { signal }),

  configuracion: (signal?: AbortSignal): Promise<GoalSettings> =>
    apiClient.get<GoalSettings>('/api/goals/settings', { signal }),

  productos: (signal?: AbortSignal): Promise<GoalOption[]> =>
    apiClient.get<GoalOption[]>('/api/goals/fuel_grades', { signal }),

  lados: (signal?: AbortSignal): Promise<GoalOption[]> =>
    apiClient.get<GoalOption[]>('/api/goals/pumps', { signal }),

  usuarios: (signal?: AbortSignal): Promise<GoalOption[]> =>
    apiClient.get<GoalOption[]>('/api/goals/users', { signal }),

  crear: (payload: GoalPayload): Promise<{ message: string; goal: Goal }> =>
    apiClient.post<{ message: string; goal: Goal }>('/api/goals', { body: payload }),

  actualizar: (id: number, payload: GoalPayload): Promise<{ message: string; goal: Goal }> =>
    apiClient.put<{ message: string; goal: Goal }>(`/api/goals/${id}`, { body: payload }),

  eliminar: (id: number): Promise<{ message: string }> =>
    apiClient.delete<{ message: string }>(`/api/goals/${id}`),

  /** Recalcula el avance a partir de la operación real y lo persiste. */
  recalcular: (id: number): Promise<GoalProgress> =>
    apiClient.post<GoalProgress>(`/api/goals/${id}/progress/update`, { body: {} }),

  evaluar: (id: number, payload: EvaluationPayload): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>(`/api/goals/${id}/evaluations`, {
      body: { ...payload, is_on_track: payload.is_on_track ? 1 : 0 },
    }),

  cambiarEstado: (id: number, status: GoalStatus): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>(`/api/goals/${id}/status`, { body: { status } }),

  marcarCumplida: (id: number, userId: number | null): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>(`/api/goals/${id}/mark_completed`, {
      body: { user_id: userId },
    }),

  guardarConfiguracion: (settings: Partial<GoalSettings>): Promise<GoalSettings> =>
    apiClient.put<GoalSettings>('/api/goals/settings', { body: settings }),
} as const;
